import type { QueryHookOptions } from '@apollo/client';
import { gql, useQuery } from '@apollo/client';
import _ from 'lodash';
import { useMemo } from 'react';
import USER_AVATAR_FRAGMENT from '../graphql/fragments/UserAvatar';
import type {
  AllUsersQuery,
  AllUsersQueryVariables,
} from '../graphql/generated/graphqlTypes';
import { UserType } from '../graphql/generated/graphqlTypes';

export const USE_ALL_USERS_USER_FRAGMENT = gql`
  fragment useAllUsers_user on User {
    id
    name
    email
    title
    description
    type
    ...UserAvatar
  }
  ${USER_AVATAR_FRAGMENT}
`;

export const ALL_USERS_QUERY = gql`
  query allUsers($includeDeactivated: Boolean) {
    session: currentSession {
      organization {
        id
        users(includeDeactivated: $includeDeactivated) {
          id
          ...useAllUsers_user
        }
      }
    }
  }
  ${USE_ALL_USERS_USER_FRAGMENT}
`;

const useAllUsers = (
  options: QueryHookOptions<AllUsersQuery, AllUsersQueryVariables> = {},
) => {
  const { data } = useQuery<AllUsersQuery, AllUsersQueryVariables>(
    ALL_USERS_QUERY,
    options,
  );

  const users = data?.session?.organization?.users;
  return useMemo(
    () => (options.skip ? [] : users || []),
    [users, options.skip],
  );
};

export const useAllAdmins = (...args: Parameters<typeof useAllUsers>) => {
  const allUsers = useAllUsers(...args);

  return allUsers.filter((user) => user.type === UserType.Admin);
};

export const useAllBots = (...args: Parameters<typeof useAllUsers>) => {
  const allUsers = useAllUsers(...args);

  return allUsers.filter((user) => user.type === UserType.Bot);
};

export const useAllUsersMap = (...args: Parameters<typeof useAllUsers>) => {
  const allUsers = useAllUsers(...args);
  const map = useMemo(() => _.keyBy(allUsers, 'id'), [allUsers]);

  return map;
};

export default useAllUsers;
